import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { SharepointIcon } from "@/components/common/icons/SharepointIcon/SharepointIcon"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconLoader2, IconUpload } from "@tabler/icons-react"
import { useCallback, useRef } from "react"

const ACCEPTED_EXTENSIONS = ".xls,.xlsx,.doc,.docx,.pdf,.txt,.csv"

const FileStackIllustration: React.FC = () => (
  <div className="relative h-[120px] w-[220px]">
    {/* Left card */}
    <div className="absolute left-[6px] top-[6px] flex h-[116px] w-[96px] rotate-[-7deg] flex-col rounded-[10px] border border-grey4 bg-grey3 p-3 shadow-md">
      <span className="font-mono self-start rounded border border-grey4 bg-grey1 px-1.5 py-[3px] text-[9px] font-semibold tracking-wide text-grey5">
        XLS
      </span>
      <div className="mt-auto flex flex-col gap-1">
        <div className="h-1 rounded-full bg-grey4" />
        <div className="h-1 w-4/5 rounded-full bg-grey4" />
        <div className="h-1 w-3/5 rounded-full bg-grey4" />
      </div>
    </div>

    {/* Right card */}
    <div className="absolute right-[6px] top-[4px] flex h-[116px] w-[96px] rotate-[7deg] flex-col rounded-[10px] border border-grey4 bg-grey3 p-3 shadow-md">
      <span className="font-mono self-start rounded border border-grey4 bg-grey1 px-1.5 py-[3px] text-[9px] font-semibold tracking-wide text-grey5">
        DOC
      </span>
      <div className="mt-auto flex flex-col gap-1">
        <div className="h-1 rounded-full bg-grey4" />
        <div className="h-1 w-4/5 rounded-full bg-grey4" />
        <div className="h-1 w-3/5 rounded-full bg-grey4" />
      </div>
    </div>

    {/* Center card (highlighted) */}
    <div className="absolute left-1/2 top-0 z-2 flex h-[116px] w-[96px] -translate-x-1/2 flex-col rounded-[10px] border border-yellow1/35 bg-grey3 p-3 shadow-md">
      <span className="font-mono self-start rounded border border-yellow1/35 bg-yellow1/6 px-1.5 py-[3px] text-[9px] font-semibold tracking-wide text-yellow1">
        PDF
      </span>
      <div className="mt-auto flex flex-col gap-1">
        <div className="h-1 rounded-full bg-grey4" />
        <div className="h-1 w-4/5 rounded-full bg-grey4" />
        <div className="h-1 w-3/5 rounded-full bg-grey4" />
      </div>
    </div>

    {/* Drop line */}
    <div
      className="absolute bottom-[-2px] left-1/2 h-[2px] w-[220px] -translate-x-1/2"
      style={{
        background:
          "linear-gradient(90deg, transparent, var(--grey4), transparent)",
      }}
    />
  </div>
)

interface KnowledgebaseEmptyStateProps {
  onFilesSelected: (files: File[]) => void
  onConnectSharePoint?: () => void
  uploadingCount: number
  /** Hides the upload primary action when the member lacks KB_UPLOAD. */
  canUpload?: boolean
}

export const KnowledgebaseEmptyState: React.FC<
  KnowledgebaseEmptyStateProps
> = ({
  onFilesSelected,
  onConnectSharePoint,
  uploadingCount,
  canUpload = true,
}) => {
  const { translate } = useTranslation()
  const sharepointEnabled = useFeatureFlag("SHAREPOINT")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isUploading = uploadingCount > 0

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (!files.length) return
      onFilesSelected(files)
      e.target.value = ""
    },
    [onFilesSelected],
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS}
        className="hidden"
        onChange={handleFileChange}
      />
      <EmptyState
        bordered
        hoverable
        illustration={<FileStackIllustration />}
        title={translate("KNOWLEDGEBASE/EMPTY_STATE_TITLE")}
        description={
          <>
            <span className="font-medium text-white">
              {translate("KNOWLEDGEBASE/EMPTY_STATE_DRAG_HINT")}
            </span>
            {", "}
            {translate("KNOWLEDGEBASE/EMPTY_STATE_DESCRIPTION")}
          </>
        }
        primaryAction={
          canUpload
            ? {
                label: translate("KNOWLEDGEBASE/HEADER_UPLOAD"),
                icon: isUploading ? (
                  <IconLoader2 size={14} className="animate-spin" />
                ) : (
                  <IconUpload size={14} />
                ),
                onClick: () => fileInputRef.current?.click(),
                disabled: isUploading,
              }
            : undefined
        }
        secondaryAction={
          sharepointEnabled && onConnectSharePoint
            ? {
                label: translate(
                  "KNOWLEDGEBASE/EMPTY_STATE_CONNECT_SHAREPOINT",
                ),
                icon: <SharepointIcon size={14} />,
                onClick: onConnectSharePoint,
              }
            : undefined
        }
      />
    </>
  )
}
